import * as React from 'react';
import * as ResizeListener from './ResizeListener';
import { createTextCell, TextCellErrors, createTextCellKey, createTextInputId } from './TextDataGrid.TextCell';
import { createCell as createDefaultCell } from './DataGrid.DefaultCell';
import { createHeader } from './DataGrid.SortHeaderCell';
import { filterAndSort, FilterCriteria } from './DataGrid.Utils';
import { Binding } from 'core/infrastructure';
import { applyColumnFormat, DataGridColumn } from './DataGrid.Column';
import { PropertyType } from 'core/infrastructure/Binding';
import * as StatePersister from './DataGrid.StatePersister';

import { Table, Column } from 'fixed-data-table-2';

interface Props<T> {
	id: string;
	columns: DataGridColumn<T>[];
	initialData: T[];
	headerHeight?: number;

	// Valor usado para comparar formar los cellKeys de las celdas
	itemKey: (item: T) => any;

	// Permite indicar que la altura de la columnas se calculará dinámicamente (penaliza el rendimiento)
	variableRowHeight?: boolean;

	// Ancho en pixels de la tabla. Si no se especifica ancho en pixels, se tomará el del padre
	width?: number;
	height?: number;
	maxHeight?: number;
	propertyType?: PropertyType;
}


interface State<T> {
	searchText: string;
	criteria: (item: T) => boolean;

	scrollToRow: number;
	scrollToColumn: number;

	selectedRowIndex: number;

	filteredData: T[];
	// Anchura. Puede haber sido calculada dinámicamente a partir del padre
	width: number;
	columnWidths: { [columnKey: string]: number };
}

export class TextDataGrid<T> extends React.Component<Props<T>, State<T>> {

	private mounted = false;
	private observer: Nullable<MutationObserver> = null;
	private ref = React.createRef<HTMLDivElement>();

	private cellsErrors: TextCellErrors = {};

	constructor(props: Props<T>) {
		super(props);

		this.state = {
			searchText: '',
			criteria: () => true,
			filteredData: [], // Se calcula en el getDerivedStateFromProps
			width: props.width || 600,
			scrollToColumn: 0,
			scrollToRow: 0,
			selectedRowIndex: -1,
			columnWidths: {}
		};

		this.onUpdateWidth = this.onUpdateWidth.bind(this);
		this.onTextCellTab = this.onTextCellTab.bind(this);
		this.onTextCellInverseTab = this.onTextCellInverseTab.bind(this);
		this.onTextCellGetFocus = this.onTextCellGetFocus.bind(this);
		this.onTextCellArrowDown = this.onTextCellArrowDown.bind(this);
		this.onTextCellArrowUp = this.onTextCellArrowUp.bind(this);
		this.onTextCellArrowRight = this.onTextCellArrowRight.bind(this);
		this.onTextCellArrowLeft = this.onTextCellArrowLeft.bind(this);
		this.onColumnResize = this.onColumnResize.bind(this);
	}

	componentDidMount(): void {

		this.mounted = true;

		const parent = this.ref.current!.parentNode as Element;
		ResizeListener.add(parent as Element, this.onUpdateWidth);

		// Necesito observar cambios (y además a lo bestia) por escenarios raros,
		// como cuando arranca el grid dentro de un panel collapsado, y su parent
		// no tiene tamaño definido todavía por no ser visible.
		this.observer = new MutationObserver(() => {
			this.onUpdateWidth();
		});

		this.observer.observe(document.body, { attributes: true, subtree: true });

		// ¿Por qué hace falta esto? Ni idea. En teoría, con ponerlo ahora y además engancharme al evento
		// de Resize del padre debería bastar, pero en el caso de los diálogos no es suficiente, así que
		// programo otra actualización para el siguiente ciclo de repintado

		const state = StatePersister.load(this.props.id, this.props.columns);
		setTimeout(() => {
			const width = this.calculateWidth(this.props);
			if (width !== undefined)
				this.setState({ width, columnWidths: state ? state.columnWidths : {} });
		}, 0);
	}

	componentWillUnmount() {
		this.mounted = false;

		const parent = this.ref.current!.parentNode as Element;
		ResizeListener.remove(parent);
		if (this.observer)
			this.observer.disconnect();
	}

	static getDerivedStateFromProps<T>(props: Props<T>, state: State<T>): Nullable<Partial<State<T>>> {
		let filteredData = filterAndSort(props.initialData, props.columns, state.searchText || '', [], null, null);
		filteredData = filterAndSort(filteredData, props.columns, state.criteria, [], null, null);
		return { filteredData };
	}

	componentDidUpdate() {
		this.onUpdateWidth();

		StatePersister.save(this.props.id, {
			sortBy: null,
			sortDirection: null,
			selectedKeys: [],
			searchText: '',
			scrollPosition: { x: 0, y: 0 },
			columnWidths: this.state.columnWidths,
			columnFilter: []
		});
	}

	filterBy(searchTextOrCriteria: string | FilterCriteria<T>) {

		if (typeof searchTextOrCriteria === 'string')
			this.filterByText(searchTextOrCriteria);
		else
			this.filterByCiteria(searchTextOrCriteria);
	}

	private filterByText(searchText: string) {
		let filteredData = filterAndSort(this.props.initialData, this.props.columns, searchText, [], null, null);
		filteredData = filterAndSort(filteredData, this.props.columns, this.state.criteria, [], null, null);
		this.setState({ filteredData, searchText }, () => this.updateCellErrors());
	}

	private filterByCiteria(criteria: (item: T) => boolean) {
		let filteredData = filterAndSort(this.props.initialData, this.props.columns, criteria, [], null, null);
		filteredData = filterAndSort(filteredData, this.props.columns, this.state.searchText, [], null, null);
		this.setState({ filteredData, criteria }, () => this.updateCellErrors());
	}

	private calculateWidth(props: Props<T>) {
		return props.width || $(this.ref.current!).parent().width();
	}

	private updateCellErrors() {

		const filteredDataItemKeys = this.state.filteredData.map(x => this.props.itemKey(x));
		const newErrors: TextCellErrors = {};

		Object.keys(this.cellsErrors).forEach(cellKey => {
			const itemKey = this.cellsErrors[cellKey].itemKey;
			const columnIndex = parseInt(cellKey.split('_')[2]);
			const rowIndex = filteredDataItemKeys.indexOf(itemKey);
			if (rowIndex >= 0)
				newErrors[createTextCellKey(itemKey, rowIndex, columnIndex)] = this.cellsErrors[cellKey];
		});

		this.cellsErrors = newErrors;
		this.forceUpdate();
	}

	private onUpdateWidth() {
		if (this.mounted) {
			// Optimizo el refresco porque eso se puede llamar muchas veces
			const newWidth = this.calculateWidth(this.props);
			const currentWidth = this.state.width;
			if (newWidth !== currentWidth && newWidth !== undefined)
				this.setState({ width: newWidth });
		}
	}

	isValid() {
		return Object.keys(this.cellsErrors).every(k => this.cellsErrors[k].errorMsg.length === 0);
	}

	getData(): T[] {
		return this.props.initialData;
	}

	getFilteredData(): T[] {
		return this.state.filteredData;
	}

	getActiveRowData(): Nullable<T> {
		return this.state.selectedRowIndex >= 0 ? this.getData()[this.state.selectedRowIndex] : null;
	}

	private onTextCellGetFocus(rowIndex: number, _columnIndex: number) {
		this.setState({ selectedRowIndex: rowIndex });
	}

	private onTextCellTab(rowIndex: number, columnIndex: number, allowsRowCycle?: boolean) {

		const rowCount = this.state.filteredData.length;

		const columnIndexes = this.props.columns.map((x, index) => ({ editable: x.editable, index }))
			.filter(x => x.editable)
			.map(x => x.index);

		const isLastColumn = columnIndex === columnIndexes[columnIndexes.length - 1];
		const isLastRow = rowIndex + 1 === rowCount;

		// Si estoy en la última columna y en la última fila, no puedo ir hacia delante
		if (isLastRow && isLastColumn)
			return;


		let nextColumnIndex = columnIndexes[(columnIndexes.indexOf(columnIndex) + 1) % columnIndexes.length];
		let nextRowIndex = columnIndexes.indexOf(columnIndex) === columnIndexes.length - 1 ? (rowIndex + 1) % rowCount : rowIndex;

		// Compruebo si puedo cambiar de filas
		const canCycle = typeof allowsRowCycle === 'undefined' || allowsRowCycle;
		nextRowIndex = canCycle ? nextRowIndex : rowIndex;
		nextColumnIndex = !canCycle && isLastColumn ? columnIndex : nextColumnIndex;

		this.setState({
			scrollToRow: nextRowIndex,
			scrollToColumn: nextColumnIndex
		}, () => {
			// Selecciono el siguiente input
			const id = createTextInputId(nextRowIndex, nextColumnIndex);
			$(`#${id}`, this.ref.current!).focus();
		});
	}

	private onTextCellInverseTab(rowIndex: number, columnIndex: number, allowsRowCycle?: boolean) {

		const editableColumnIndexes = this.props.columns.map((x, index) => ({ editable: x.editable, index }))
			.filter(x => x.editable)
			.map(x => x.index);

		const isFirstRow = rowIndex === 0;
		const isFirstColumn = editableColumnIndexes.indexOf(columnIndex) === 0;

		// Si estoy en la primera columna y en la primera fila, no puedo ir hacia atrás
		if (isFirstRow && isFirstColumn)
			return;

		const lastColumnIndex = editableColumnIndexes[editableColumnIndexes.length - 1];
		const previousColumnIndex = editableColumnIndexes[editableColumnIndexes.indexOf(columnIndex) - 1];

		let nextColumnIndex = isFirstColumn ? lastColumnIndex : previousColumnIndex;
		let nextRowIndex = isFirstColumn ? rowIndex - 1 : rowIndex;

		// Compruebo si puedo cambiar de fila
		const canCycle = typeof allowsRowCycle === 'undefined' || allowsRowCycle;
		nextRowIndex = canCycle ? nextRowIndex : rowIndex;
		nextColumnIndex = !canCycle && isFirstColumn ? columnIndex : nextColumnIndex;

		this.setState({
			scrollToRow: nextRowIndex,
			scrollToColumn: nextColumnIndex
		}, () => {
			const id = createTextInputId(nextRowIndex, nextColumnIndex);
			$(`#${id}`, this.ref.current!).focus();
		});
	}

	private onTextCellArrowUp(rowIndex: number, columnIndex: number) {

		const isFirstRow = rowIndex === 0;
		const nextRowIndex = isFirstRow ? rowIndex : rowIndex - 1;

		this.setState({
			scrollToRow: nextRowIndex,
			scrollToColumn: columnIndex
		}, () => {
			// Selecciono el siguiente input
			const id = createTextInputId(nextRowIndex, columnIndex);
			$(`#${id}`, this.ref.current!).focus();
		});
	}

	private onTextCellArrowDown(rowIndex: number, columnIndex: number) {

		const rowCount = this.state.filteredData.length;
		const isLastRow = rowIndex + 1 === rowCount;

		const nextRowIndex = isLastRow ? rowIndex : rowIndex + 1;

		this.setState({
			scrollToRow: nextRowIndex,
			scrollToColumn: columnIndex
		}, () => {
			// Selecciono el siguiente input
			const id = createTextInputId(nextRowIndex, columnIndex);
			$(`#${id}`, this.ref.current!).focus();
		});
	}

	private onTextCellArrowLeft(rowIndex: number, columnIndex: number) {
		this.onTextCellInverseTab(rowIndex, columnIndex, false);
	}

	private onTextCellArrowRight(rowIndex: number, columnIndex: number) {
		this.onTextCellTab(rowIndex, columnIndex, false);
	}

	private onColumnResize(newColumnWidth: number, columnKey: string): void {
		this.setState(({ columnWidths }: State<T>) => ({
			columnWidths: {
				...columnWidths,
				[columnKey]: newColumnWidth
			}
		}));
	}

	render() {


		const getColumnWidth = (col: DataGridColumn<T>) => {
			const defaultColWidth = 200;
			if (this.state.columnWidths[col.title])
				return this.state.columnWidths[col.title];

			// Si no se ha redimensionado la columna y es la última, añado 50px de ancho para
			// dejar hueco a la barra de scroll y los botones de operación
			const BUTTONS_WIDTH = 50;
			const width = col.width || defaultColWidth;
			const isLastColumn = col === this.props.columns[this.props.columns.length - 1];
			return isLastColumn ? width + BUTTONS_WIDTH : width;
		};

		// Si ninguna columna tiene puesto flexGrow, reparto el espacio proporcionalmente
		const defaultFlexGrow = this.props.columns.every(x => typeof x.flexGrow === 'undefined') ? 1 : 0;

		const createCell = (column: DataGridColumn<T>, columnIndex: number, items: T[], cellsErrors: TextCellErrors) => {
			return column.editable ? createTextCell({
				itemKey: this.props.itemKey,
				column,
				columnIndex,
				items,
				onTab: this.onTextCellTab,
				onInverseTab: this.onTextCellInverseTab,
				onArrowDown: this.onTextCellArrowDown,
				onArrowUp: this.onTextCellArrowUp,
				onArrowLeft: this.onTextCellArrowLeft,
				onArrowRight: this.onTextCellArrowRight,
				onGetFocus: this.onTextCellGetFocus,
				cellsErrors,
				propertyType: this.props.propertyType || 'string'
			}) : createDefaultCell({ column, items, disableFocus: true, isLastColumn: false });
		};

		const columns = this.props.columns.map((col, index) => <Column
			key={col.title}
			columnKey={col.title}
			width={getColumnWidth(col)}
			minWidth={15}
			flexGrow={col.flexGrow || defaultFlexGrow}
			allowCellsRecycling={true}
			isResizable={col.isResizable || typeof col.isResizable === 'undefined'}
			fixed={col.fixed}
			header={createHeader(col, null, null)}
			cell={createCell(col, index, this.state.filteredData, this.cellsErrors)} />);

		const getSelectedRowClassName = (rowIndex: number) => {
			return this.state.selectedRowIndex === rowIndex ? 'selected' : '';
		};

		const rowHeightGetter: ((index: number) => number) | undefined = this.props.variableRowHeight === true
			? ((index: number) => {
				const item = this.state.filteredData[index];

				const lines = this.props.columns
					.map(column => applyColumnFormat(column, Binding.getValue(item, Binding.propertyPathToString(column.linkTo)), item))
					.map(formattedValue => formattedValue.split('\n').length)
					.reduce((acc, length) => Math.max(acc, length), 0);

				const PADDING = 14;
				const LINE_HEIGHT = 18;

				return PADDING + LINE_HEIGHT * lines;
			})
			: undefined;


		// Saco el foco de los inputs para que guarden sus valores. Si no lo perdemos
		const onScrollStart = () => $('input').blur();

		return <div ref={this.ref}>
			<Table
				className='editable-datatable'
				rowClassNameGetter={getSelectedRowClassName}
				touchScrollEnabled={true}
				scrollToRow={this.state.scrollToRow}
				scrollToColumn={this.state.scrollToColumn}
				rowsCount={this.state.filteredData.length}
				headerHeight={this.props.headerHeight || 36}
				rowHeightGetter={rowHeightGetter}
				rowHeight={32}
				height={this.props.height || 400}
				width={this.state.width}
				onColumnResizeEndCallback={this.onColumnResize}
				isColumnResizing={false}
				onScrollStart={onScrollStart}
				maxHeight={this.props.maxHeight || 400}>
				{columns}
			</Table>
		</div>;
	}
}

export type TextDataGridCtor<T> = new (props: Props<T>) => TextDataGrid<T>;
